// src/components/views/SmartInboxView.tsx
// Smart Inbox UI: email list, classification, and action proposal.

import { useState, useEffect } from 'react'
import { SmartInboxService, InboxEmail } from '../../services/smartInboxService'
import Icon from './icons'
import './SmartInboxView.css'

type ViewTab = 'unprocessed' | 'processed' | 'manual_review' | 'training'

const TAB_LABEL: Record<ViewTab, string> = {
  unprocessed: '📥 Inbox',
  processed: '✓ Processed',
  manual_review: '⚠️ Manual Review',
  training: '📚 Training Log',
}

export function SmartInboxView() {
  const [tab, setTab] = useState<ViewTab>('unprocessed')
  const [emails, setEmails] = useState<InboxEmail[]>([])
  const [selectedEmail, setSelectedEmail] = useState<InboxEmail | null>(null)
  const [overrideAction, setOverrideAction] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [trainingLog, setTrainingLog] = useState<any[]>([])
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    loadEmails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  async function loadEmails() {
    setLoading(true)
    try {
      if (tab === 'training') {
        setTrainingLog(await SmartInboxService.loadTrainingLog())
        setLoading(false)
        return
      }
      let data: InboxEmail[] = []
      if (tab === 'unprocessed') data = await SmartInboxService.loadUnprocessedEmails()
      else if (tab === 'processed') data = await SmartInboxService.loadProcessedEmails()
      else if (tab === 'manual_review') data = await SmartInboxService.loadManualReviewEmails()
      setEmails(data)
      setSelectedEmail(data.length ? data[0] : null)
      setOverrideAction('')
    } catch (error) {
      console.error('Error loading emails:', error)
      setToast('Could not load emails')
    } finally {
      setLoading(false)
    }
  }

  const flash = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 3200)
  }

  async function handleAcceptAction() {
    if (!selectedEmail) return
    setBusy(true)
    try {
      const res = await SmartInboxService.executeAction(
        selectedEmail.id,
        selectedEmail.predicted_action as any
      )
      if (res.success) {
        flash(
          res.result?.warning
            ? `Done, with a note: ${res.result.warning}`
            : `Action executed — ${getActionLabel(selectedEmail.predicted_action)}`
        )
      } else {
        flash(res.error || 'Action failed')
      }
      setEmails(emails.filter((e) => e.id !== selectedEmail.id))
      setSelectedEmail(null)
    } catch (error) {
      console.error(error)
      flash('Action failed')
    } finally {
      setBusy(false)
    }
  }

  async function handleDeclineAction() {
    if (!selectedEmail) return
    setBusy(true)
    try {
      await SmartInboxService.executeAction(selectedEmail.id, 'manual')
      flash('Moved to Manual Review')
      setEmails(emails.filter((e) => e.id !== selectedEmail.id))
      setSelectedEmail(null)
    } catch (error) {
      console.error(error)
    } finally {
      setBusy(false)
    }
  }

  async function handleOverride() {
    if (!selectedEmail || !overrideAction) return
    setBusy(true)
    try {
      await SmartInboxService.logTrainingCorrection(
        selectedEmail.id,
        selectedEmail.predicted_action,
        overrideAction,
        selectedEmail.predicted_confidence
      )
      await SmartInboxService.executeAction(selectedEmail.id, overrideAction as any)
      flash('Override saved & action executed')
      setEmails(emails.filter((e) => e.id !== selectedEmail.id))
      setSelectedEmail(null)
    } catch (error) {
      console.error(error)
      flash('Override failed')
    } finally {
      setBusy(false)
    }
  }

  const getActionLabel = (action: string) =>
    ({
      deal: '✨ Create New Deal',
      paid: '💰 Mark Deal as Paid',
      ppsr: '📋 PPSR Status Update',
      inspection: '🔍 Create Inspection Task',
      finance: '💳 Finance Payout Task',
      manual: '👁️ Manual Review Required',
    }[action] || action)

  const getActionDescription = (action: string) =>
    ({
      deal: 'Vehicle offered by a seller. Creates a draft deal pre-filled from the email — review & complete it in the pipeline.',
      paid: 'Payment confirmation detected. Marks the associated deal as paid.',
      ppsr: 'PPSR certificate or status report. Updates the deal PPSR record.',
      inspection: 'Vehicle inspection report. Creates an inspection task.',
      finance: 'Finance payout information. Creates a finance task.',
      manual: 'Low confidence or no matching rules. Requires manual review.',
    }[action] || '')

  const Tabs = () => (
    <div className="tabs">
      {(['unprocessed', 'processed', 'manual_review', 'training'] as ViewTab[]).map((t) => (
        <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
          {t === 'unprocessed' ? `${TAB_LABEL[t]} (${tab === 'unprocessed' ? emails.length : ''})`.replace(' ()', '') : TAB_LABEL[t]}
        </button>
      ))}
    </div>
  )

  return (
    <div className="inbox-view">
      <div className="page-header">
        <div className="eyebrow"><Icon name="inbox" size={13} /> Smart Inbox</div>
        <h2>{tab === 'training' ? 'Classification Corrections' : 'Incoming Emails'}</h2>
        <p>
          {tab === 'training'
            ? 'Record of user corrections used to improve the rules over time.'
            : 'Rule-based classification suggests an action for each email. Accept, decline, or override — corrections train the rules.'}
        </p>
      </div>

      <Tabs />

      {toast && <div className="inbox-toast">{toast}</div>}

      {tab === 'training' ? (
        <div className="training-log">
          {loading ? (
            <div className="spinner">Loading…</div>
          ) : trainingLog.length === 0 ? (
            <p className="empty-state">No corrections yet. Override a classification to start training the system.</p>
          ) : (
            trainingLog.map((c) => (
              <div key={c.id} className="training-item">
                <div className="training-item-head">
                  <span className="from">{c.inbox_emails?.from_address || 'Unknown'}</span>
                  <span className="time">{new Date(c.corrected_at).toLocaleDateString()}</span>
                </div>
                <div className="training-item-subject">{c.inbox_emails?.subject || 'No subject'}</div>
                <div className="training-actions">
                  <span className="predicted">
                    {c.predicted_action} ({c.predicted_confidence}%)
                  </span>
                  <span className="arrow">→</span>
                  <span className="actual">{c.actual_action}</span>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="inbox-layout">
          <div className="email-list">
            {loading && emails.length === 0 ? (
              <div className="spinner">Loading emails…</div>
            ) : emails.length === 0 ? (
              <div className="empty-state">No emails in this view.</div>
            ) : (
              emails.map((email) => (
                <div
                  key={email.id}
                  className={`email-item ${selectedEmail?.id === email.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedEmail(email)
                    setOverrideAction('')
                  }}
                >
                  <div className="email-from">{email.from_address}</div>
                  <div className="email-subject">{email.subject}</div>
                  <div className="email-time">
                    {new Date(email.received_at).toLocaleString([], {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {selectedEmail && (
            <div className="email-detail-panel">
              <div className="email-detail">
                <div className="email-detail-head">
                  <div className="email-detail-from">From: {selectedEmail.from_address}</div>
                  <div className="email-detail-subject">{selectedEmail.subject}</div>
                  <div className="email-detail-time">
                    Received: {new Date(selectedEmail.received_at).toLocaleString()}
                  </div>
                </div>

                <div className="email-body">{selectedEmail.body_clean}</div>

                <div className="classification">
                  <div className="classification-title">Classification</div>

                  <div className="rules-applied">
                    {(selectedEmail.rules_applied || []).slice(0, 3).map((rule, i) => (
                      <div key={i} className="rule-item">
                        <div className="rule-check"><Icon name="check" size={14} /></div>
                        <div className="rule-text">{rule}</div>
                      </div>
                    ))}
                    {(selectedEmail.rules_applied?.length || 0) > 3 && (
                      <div className="rule-item">
                        <div className="rule-check">+</div>
                        <div className="rule-text">
                          {selectedEmail.rules_applied.length - 3} more signals matched
                        </div>
                      </div>
                    )}
                    {(!selectedEmail.rules_applied || selectedEmail.rules_applied.length === 0) && (
                      <div className="rule-item">
                        <div className="rule-text" style={{ color: 'var(--txt3)' }}>
                          No strong signals — manual review recommended.
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="confidence">
                    Confidence: {selectedEmail.predicted_confidence}%
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{
                          width: `${selectedEmail.predicted_confidence}%`,
                          background:
                            selectedEmail.predicted_confidence >= 70 ? 'var(--green)' : 'var(--amber)',
                        }}
                      />
                    </div>
                  </div>

                  {selectedEmail.predicted_confidence >= 70 ? (
                    <>
                      <div className="suggested-action">
                        <div className="action-label">✨ Suggested Action</div>
                        <div className="action-title">{getActionLabel(selectedEmail.predicted_action)}</div>
                        <div className="action-desc">{getActionDescription(selectedEmail.predicted_action)}</div>
                        {tab === 'unprocessed' && (
                          <div className="action-buttons">
                            <button className="btn primary" onClick={handleAcceptAction} disabled={busy}>
                              <Icon name="check" size={14} /> Accept &amp; Execute
                            </button>
                            <button className="btn" onClick={handleDeclineAction} disabled={busy}>
                              <Icon name="x" size={14} /> Decline
                            </button>
                          </div>
                        )}
                      </div>

                      {tab === 'unprocessed' && (
                        <div className="override-section">
                          <div className="override-label">✎ Override Classification</div>
                          <select
                            className="override-select"
                            value={overrideAction}
                            onChange={(e) => setOverrideAction(e.target.value)}
                          >
                            <option value="">Disagree? Select the correct action…</option>
                            <option value="deal">→ Create New Deal</option>
                            <option value="paid">→ Mark Deal as Paid</option>
                            <option value="ppsr">→ PPSR Status Update</option>
                            <option value="inspection">→ Create Inspection Task</option>
                            <option value="finance">→ Finance Payout Task</option>
                            <option value="manual">→ Manual Review</option>
                          </select>
                          <button className="override-btn" onClick={handleOverride} disabled={!overrideAction || busy}>
                            Save Override &amp; Train
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="low-confidence">
                      <div className="low-confidence-label">⚠️ Low Confidence</div>
                      <p>Classification is below the 70% threshold. Please review this email manually.</p>
                      {tab === 'unprocessed' && (
                        <div className="override-section" style={{ marginTop: 12, textAlign: 'left' }}>
                          <div className="override-label">Choose an action</div>
                          <select
                            className="override-select"
                            value={overrideAction}
                            onChange={(e) => setOverrideAction(e.target.value)}
                          >
                            <option value="">Select the correct action…</option>
                            <option value="deal">→ Create New Deal</option>
                            <option value="paid">→ Mark Deal as Paid</option>
                            <option value="ppsr">→ PPSR Status Update</option>
                            <option value="inspection">→ Create Inspection Task</option>
                            <option value="finance">→ Finance Payout Task</option>
                            <option value="manual">→ Manual Review</option>
                          </select>
                          <button className="override-btn" onClick={handleOverride} disabled={!overrideAction || busy}>
                            Save &amp; Execute
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SmartInboxView
